import { useEffect } from "react";

const CHUNK_RELOAD_KEY = "chunk_reload_attempted";

function isChunkLoadError(error) {
  if (!error) return false;
  const msg = error?.message || "";
  return (error?.name === "ChunkLoadError" || msg?.includes("Failed to fetch dynamically imported module") ||
  msg?.includes("Importing a module script failed") ||
  msg?.includes("Unable to preload CSS") ||
  msg?.includes("error loading dynamically imported module") || msg?.includes("dynamically imported module"));
}

function attemptChunkReload() {
  const reloadAttempted = sessionStorage.getItem(CHUNK_RELOAD_KEY);
  if (!reloadAttempted) {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
    window.location?.reload();
    return true;
  }
  return false;
}

/**
 * Detects dynamic import (chunk load) failures caused by stale cached assets
 * after a new deployment, and automatically reloads the page once to recover.
 *
 * Handles both:
 * 1. Unhandled promise rejections / window errors (async import failures)
 * 2. Errors caught by React ErrorBoundary (via window.__COMPONENT_ERROR__ hook)
 */
const ChunkErrorHandler = () => {
  useEffect(() => {
    // If we previously set the reload flag and the page loaded successfully
    // (i.e., we got here without a chunk error), clear the flag so future
    // deploys can trigger a reload again.
    const alreadyAttempted = sessionStorage.getItem(CHUNK_RELOAD_KEY);
    if (alreadyAttempted) {
      sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    }

    const handleError = (event) => {
      const error = event?.error || event?.reason;
      if (!error) return;
      if (isChunkLoadError(error)) {
        attemptChunkReload();
      }
    };

    // Catch unhandled promise rejections (dynamic import failures surface here)
    window.addEventListener("unhandledrejection", handleError);
    // Catch synchronous errors
    window.addEventListener("error", handleError);

    // Hook into ErrorBoundary's componentDidCatch — ErrorBoundary calls
    // window.__COMPONENT_ERROR__(error, errorInfo) for every caught error.
    // This catches chunk errors that React Suspense swallows internally.
    const prevComponentError = window.__COMPONENT_ERROR__;
    window.__COMPONENT_ERROR__ = (error, errorInfo) => {
      if (isChunkLoadError(error)) {
        attemptChunkReload();
      }
      // Call any previously registered handler
      prevComponentError?.(error, errorInfo);
    };

    return () => {
      window.removeEventListener("unhandledrejection", handleError);
      window.removeEventListener("error", handleError);
      // Restore previous handler
      window.__COMPONENT_ERROR__ = prevComponentError;
    };
  }, []);

  return null;
};

export default ChunkErrorHandler;
